import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import moment from "moment";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Button,
  Input,
  Alert,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge
} from "reactstrap";

import { Enviroments } from "../enviroments/enviroments";
import InvestmentTable from "../components/InvestmentTable/InvestmentTable";
import HistoryOdds from "../components/HistoryOdds/HistoryOdds";
import HistoryOddsPinks from "../components/HistoryOddsPinks/HistoryOddsPinks";
import HistoryHours from "../components/HistoryHours/HistoryHours";
import { SimpleTableView } from "backedComponents/SimpleTable/SimpleTableView";
import LogRocket from "logrocket";

import { cactusApi } from "../services/cactusApi";

/* -------------------- Tema / helpers -------------------- */
const COLORS = {
  bg: "#0B0B0F",
  surface: "#121317",
  border: "#26272B",
  text: "#ECECF3",
  sub: "#A3A6AF",
  primary: "#913ef8",
  yellow: "#F6E05E"
};

const Shimmer = ({ width = "100%", height = 16, radius = 6, className = "" }) => (
  <div
    className={className}
    style={{
      width,
      height,
      borderRadius: radius,
      background:
        "linear-gradient(90deg, rgba(255,255,255,0.06) 25%, rgba(255,255,255,0.12) 37%, rgba(255,255,255,0.06) 63%)",
      backgroundSize: "400% 100%",
      animation: "shimmer 1.2s ease-in-out infinite"
    }}
  />
);

const GlobalStyles = () => (
  <style>{`
    @keyframes shimmer {
      0% { background-position: 100% 0; }
      100% { background-position: -100% 0; }
    }
    .dash-toolbar {
      position: sticky;
      top: 0;
      z-index: 5;
      border: 1px solid ${COLORS.border};
      background: ${COLORS.surface};
    }
    .nav-tabs .nav-link {
      cursor: pointer;
      color: ${COLORS.sub};
    }
    .nav-tabs .nav-link.active {
      color: ${COLORS.text};
      background: transparent;
      border-color: ${COLORS.primary} ${COLORS.border} transparent;
    }
  `}</style>
);

/* -------------------- Componente -------------------- */

const Dashboard = () => {
  const dateNow = useMemo(() => moment(new Date()).format("YYYY-MM-DD"), []);
  const [betHouse, setBetHouse] = useState("MINES_BET");
  const [numberVelas, setNumberVelas] = useState(10);
  const [activeTab, setActiveTab] = useState("1");
  const [analysisHistoryByOdd, setAnalysisHistoryByOdd] = useState([]);
  const [analysisHistoryMostPositionsAndQuantity, setAnalysisHistoryMostPositionsAndQuantity] = useState([]);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState("");
  const [analysisHistoryResultsAviator, setAnalysisHistoryResultsAviator] = useState([]);
  const [urlAviator, setUrlAviator] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Identificação LogRocket
  useEffect(() => {
    LogRocket.identify(localStorage.getItem("id") || "nao encontrado", {
      useId: localStorage.getItem("id") || "nao encontrado",
      email: localStorage.getItem("email") || "nao encontrado"
    });
  }, []);

  const handleIframeUrl = () => (urlAviator === "" ? "https://esportiva.bet.br" : urlAviator);

  // --- helper para mapear casa -> id do jogo ---
  const getGameIdByHouse = (house) => (house === "MINES_BET" ? "3835" : "7613");

  // --- startCasinoGame atualizado ---
  const startCasinoGame = useCallback(
    async (house = betHouse) => {
      const isMobileDevice = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ? 1 : 0;
      const gameId = "spribe/aviator"; // slug do jogo

      try {
        setUrlAviator("");
        const response = await cactusApi.startGame({
          slug: gameId,
          platform: isMobileDevice ? "MOBILE" : "WEB",
          use_demo: 0
        });

        setUrlAviator(response.data.game_url || "");
      } catch (error) {
        console.error("Erro ao iniciar o jogo de cassino:", error);
        alert("Sua sessão expirou ou é inválida. Por favor, faça login novamente.");
      }
    },
    [betHouse]
  );

  // dispara sempre que betHouse mudar
  useEffect(() => {
    startCasinoGame(betHouse);
  }, [betHouse, startCasinoGame]);

  /* -------------------- APIs Analíticas -------------------- */

  const getHistoryAnalysisApi = useCallback(async () => {
    try {
      const headers = {
        headers: { Authorization: `Bearer ${localStorage.getItem("apiToken")}` }
      };
      const betHouseCustom = betHouse === "BR4BET" ? "MINES_BET" : betHouse;
      const url = `${Enviroments.API_URL_NODE}analysis-odd?date=${dateNow}&betHouse=${betHouseCustom}&odd=${numberVelas}`;
      const { data } = await axios.get(url, headers);
      return data || [];
    } catch (error) {
      console.error(error);
      return [];
    }
  }, [betHouse, dateNow, numberVelas]);

  const getHistoryQuantityApi = useCallback(async () => {
    try {
      const headers = {
        headers: { Authorization: `Bearer ${localStorage.getItem("apiToken")}` }
      };
      const betHouseCustom = betHouse === "BR4BET" ? "MINES_BET" : betHouse;
      const url = `${Enviroments.API_URL_NODE}analysis-position-odd?date=${dateNow}&betHouse=${betHouseCustom}&odd=${numberVelas}`;
      const { data } = await axios.get(url, headers);
      return data || [];
    } catch (error) {
      console.error(error);
      return [];
    }
  }, [betHouse, dateNow, numberVelas]);

  const loadAnalytics = useCallback(async () => {
    setAnalysisLoading(true);
    setAnalysisError("");
    try {
      const [resAnalysis, resQuantity] = await Promise.all([
        getHistoryAnalysisApi(),
        getHistoryQuantityApi()
      ]);
      setAnalysisHistoryByOdd(resAnalysis);
      setAnalysisHistoryMostPositionsAndQuantity(resQuantity);
      setLastUpdated(moment().format("HH:mm:ss"));
    } catch {
      setAnalysisError("Não foi possível carregar as análises agora. Tente novamente.");
    } finally {
      setAnalysisLoading(false);
    }
  }, [getHistoryAnalysisApi, getHistoryQuantityApi]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const handleRefreshAll = async () => {
    setRefreshing(true);
    await Promise.all([loadAnalytics(), startCasinoGame(betHouse)]);
    setRefreshing(false);
  };

  const handleResultsUpdated = (results) => setAnalysisHistoryResultsAviator(results);

  // --- trocar gráfico ---
  const handleSelectGraph = useCallback(
    (house) => {
      if (house !== betHouse) {
        setBetHouse(house);
      }
    },
    [betHouse]
  );

  /* -------------------- Render -------------------- */

  return (
    <div className="content">
      <GlobalStyles />

      {/* Toolbar fixa */}
      <Card className="dash-toolbar mb-3">
        <CardHeader className="pb-2">
          <h5 className="title p-0 m-0">Selecione o gráfico abaixo</h5>
        </CardHeader>
        <CardBody className="pt-2">
          <div className="d-flex flex-wrap" style={{ gap: 8 }}>
            <Button
              color={betHouse === "MINES_BET" ? "primary" : "default"}
              className="btn-round"
              size="sm"
              onClick={() => handleSelectGraph("MINES_BET")}
              active={betHouse === "MINES_BET"}
            >
              Gráfico – EsportivaBet
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Abas principais */}
      <Row className="mt-3">
        <Col xs="12">
          <Card style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
            <CardHeader className="pb-0">
              <Nav tabs>
                <NavItem>
                  <NavLink className={activeTab === "1" ? "active" : ""} onClick={() => setActiveTab("1")}>
                    📈 Histórico Completo
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink className={activeTab === "2" ? "active" : ""} onClick={() => setActiveTab("2")}>
                    🌸 Velas Rosas
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink className={activeTab === "3" ? "active" : ""} onClick={() => setActiveTab("3")}>
                    ⏰ Velas Por Horário
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink className={activeTab === "0" ? "active" : ""} onClick={() => setActiveTab("0")}>
                    💰 Gestão de Banca
                  </NavLink>
                </NavItem>
              </Nav>
            </CardHeader>

            <CardBody style={{ paddingTop: 0 }}>
              <TabContent activeTab={activeTab}>
                <TabPane tabId="0">
                  <InvestmentTable />
                </TabPane>

                <TabPane tabId="1">
                  <HistoryOdds props={{ betHouse }} />
                </TabPane>

                <TabPane tabId="2">
                  <HistoryOddsPinks props={{ betHouse }} onResultsUpdated={handleResultsUpdated} />
                </TabPane>

                <TabPane tabId="3">
                  <HistoryHours props={{ betHouse }} />
                </TabPane>
              </TabContent>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* AutomaPRO */}
      <Row className="mt-3">
        <Col md="12">
          <Card style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
            <CardHeader>
              <h4 style={{ color: COLORS.text, marginBottom: 6 }}>AutomaPRO</h4>
              <div style={{ color: COLORS.sub, fontSize: 13 }}>Opere diretamente pelo dashboard.</div>
            </CardHeader>
            <CardBody className="p-0">
              <div style={{ width: "100%", height: "75vh", background: "#0F1014" }}>
                <iframe
                  title="AutomaPRO Aviator"
                  src={handleIframeUrl()}
                  style={{ border: "none", width: "100%", height: "100%" }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Relatórios */}
      <Row className="mt-3">
        <Col lg="8">
          <Card style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
            <CardHeader>
              <h5 style={{ color: COLORS.text }}>Histórico detalhado</h5>
              <h6 style={{ color: COLORS.sub }}>
                Vela: <strong style={{ color: COLORS.primary }}>{numberVelas}X</strong>
              </h6>
            </CardHeader>
            <CardBody>
              {analysisError && (
                <Alert color="danger" style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
                  {analysisError}
                </Alert>
              )}

              {analysisLoading ? (
                <>
                  <Shimmer width="60%" height={20} className="mb-2" />
                  <Shimmer width="100%" height={180} />
                </>
              ) : (
                <SimpleTableView
                  dataHeaders={["position", "intervalTime", "odd", "minutes"]}
                  data={analysisHistoryByOdd}
                />
              )}
            </CardBody>
          </Card>
        </Col>

        <Col lg="4">
          <Card style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
            <CardHeader>
              <h5 style={{ color: COLORS.text }}>Posição que repetiu a mesma odd</h5>
            </CardHeader>
            <CardBody>
              {analysisLoading ? (
                <>
                  <Shimmer width="70%" height={20} className="mb-2" />
                  <Shimmer width="100%" height={180} />
                </>
              ) : (
                <SimpleTableView
                  dataHeaders={["posicao", "quantidade", "porcentagem"]}
                  data={analysisHistoryMostPositionsAndQuantity}
                />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
